/**
 * GameOverScreen.ts
 * Shown when the enemy holds the Beacon long enough to kill the signal.
 *
 * Displays survival stats (time, waves, kills, venues, song drops) and
 * offers RETRY (reload) and MAIN MENU (reload) buttons.
 */

import { Container, Graphics, Text, TextStyle } from 'pixi.js';

const OVERLAY_COL  = 0x060306;
const PANEL_BG     = 0x100a10;
const PANEL_BORDER = 0x5a1a1a;
const DANGER       = 0xff3333;
const DIM_RED      = 0x7a2a2a;
const MUTED        = 0x4a3a3a;
const BTN_BG       = 0x1a0a0a;
const BTN_HOVER    = 0x2a1010;
const ACCENT       = 0x00ffcc;

export interface GameStats {
  sceneScore:      number;
  sceneTier:       string;
  timeSurvived:    number; // seconds
  wavesRepelled:   number;
  enemiesDefeated: number;
  venuesClaimed:   number;
  songDrops:       number;
  mapCode:         string; // unique map identifier — share to replay this layout
}

export class GameOverScreen {
  private readonly container = new Container();
  private screenW = 1280;
  private screenH = 720;

  constructor(
    ui: Container,
    private readonly onRetry:    () => void,
    private readonly onMainMenu: () => void,
  ) {
    ui.addChild(this.container);
    this.container.visible = false;
  }

  show(w: number, h: number, stats: GameStats): void {
    this.screenW = w;
    this.screenH = h;
    this.rebuild(stats);
    this.container.visible = true;
  }

  hide(): void {
    this.container.visible = false;
  }

  resize(w: number, h: number, stats?: GameStats): void {
    this.screenW = w;
    this.screenH = h;
    if (this.container.visible && stats) this.rebuild(stats);
  }

  get isVisible(): boolean { return this.container.visible; }

  // ── Private ──────────────────────────────────────────────────────────────

  private rebuild(stats: GameStats): void {
    this.container.removeChildren();

    const W = this.screenW, H = this.screenH;
    const cx = W / 2, cy = H / 2;

    // ── Full overlay ──────────────────────────────────────────────────────────
    const overlay = new Graphics();
    overlay.beginFill(OVERLAY_COL, 0.92);
    overlay.drawRect(0, 0, W, H);
    overlay.endFill();
    this.container.addChild(overlay);

    // ── Panel ─────────────────────────────────────────────────────────────────
    const PW = Math.min(380, W - 32), PH = 430;
    const px = cx - PW / 2, py = cy - PH / 2;

    const panel = new Graphics();
    panel.beginFill(PANEL_BG, 0.98);
    panel.lineStyle(1, PANEL_BORDER, 0.9);
    panel.drawRoundedRect(px, py, PW, PH, 4);
    panel.endFill();
    panel.lineStyle(0);
    this.container.addChild(panel);

    // ── Header bar ───────────────────────────────────────────────────────────
    const hdr = new Graphics();
    hdr.beginFill(0x2a0808, 0.9);
    hdr.drawRoundedRect(px, py, PW, 60, 4);
    hdr.endFill();
    this.container.addChild(hdr);

    const title = new Text('SIGNAL LOST', new TextStyle({
      fontFamily: 'Menlo, Consolas, monospace',
      fontSize:   22,
      fontWeight: 'bold',
      fill:       '#ff3333',
      letterSpacing: 6,
    }));
    title.anchor.set(0.5, 0.5);
    title.position.set(cx, py + 30);
    this.container.addChild(title);

    // ── Tagline ───────────────────────────────────────────────────────────────
    const sub = new Text('The Beacon has fallen. The scene goes quiet.', new TextStyle({
      fontFamily: 'Menlo, Consolas, monospace',
      fontSize:   10,
      fill:       '#5a3a3a',
      letterSpacing: 1,
    }));
    sub.anchor.set(0.5, 0);
    sub.position.set(cx, py + 68);
    this.container.addChild(sub);

    // ── Stats grid ────────────────────────────────────────────────────────────
    const statRows: Array<[string, string]> = [
      ['Scene score',      String(stats.sceneScore)],
      ['Scene status',     stats.sceneTier],
      ['Time survived',    this.formatTime(stats.timeSurvived)],
      ['Waves repelled',   String(stats.wavesRepelled)],
      ['Enemies defeated', String(stats.enemiesDefeated)],
      ['Venues claimed',   String(stats.venuesClaimed)],
      ['Song drops',       String(stats.songDrops)],
    ];

    const rowH  = 34;
    const rowY0 = py + 94;
    const lx    = px + 26;
    const rx    = px + PW - 26;

    for (let i = 0; i < statRows.length; i++) {
      const [label, value] = statRows[i];
      const y = rowY0 + i * rowH;

      // Alternating row tint
      if (i % 2 === 0) {
        const rowBg = new Graphics();
        rowBg.beginFill(0x160a0a, 0.5);
        rowBg.drawRect(px + 4, y, PW - 8, rowH);
        rowBg.endFill();
        this.container.addChild(rowBg);
      }

      const lbl = new Text(label, new TextStyle({
        fontFamily: 'Menlo, Consolas, monospace',
        fontSize: 11, fill: '#5a3a3a',
      }));
      lbl.anchor.set(0, 0.5);
      lbl.position.set(lx, y + rowH / 2);
      this.container.addChild(lbl);

      const valColour = value === '0' ? '#2a2020' : '#c9fff8';
      const val = new Text(value, new TextStyle({
        fontFamily: 'Menlo, Consolas, monospace',
        fontSize: 13, fontWeight: 'bold', fill: valColour,
      }));
      val.anchor.set(1, 0.5);
      val.position.set(rx, y + rowH / 2);
      this.container.addChild(val);
    }

    // Rule above buttons
    const rule = new Graphics();
    rule.lineStyle(1, PANEL_BORDER, 0.4);
    rule.moveTo(px + 20, rowY0 + statRows.length * rowH + 4);
    rule.lineTo(px + PW - 20, rowY0 + statRows.length * rowH + 4);
    this.container.addChild(rule);

    // Map code — small footnote so the player can note it down to replay
    const codeLabel = new Text(`map  ${stats.mapCode}`, new TextStyle({
      fontFamily: 'Menlo, Consolas, monospace',
      fontSize: 10,
      fill: '#37e0d0',
      letterSpacing: 2,
    }));
    codeLabel.alpha = 0.55;
    codeLabel.anchor.set(0.5, 0);
    codeLabel.position.set(cx, rowY0 + statRows.length * rowH + 10);
    this.container.addChild(codeLabel);

    // ── Buttons ───────────────────────────────────────────────────────────────
    const btnY   = rowY0 + statRows.length * rowH + 38;
    // Keep buttons within the panel — gap shrinks on narrow screens
    const btnGap = Math.min(100, Math.floor(PW / 4));
    this.addBtn(cx - btnGap, btnY + 20, '↺  RETRY', DANGER, this.onRetry);
    this.addBtn(cx + btnGap, btnY + 20, '←  MENU',  MUTED,  this.onMainMenu);
  }

  private addBtn(cx: number, cy: number, label: string, colour: number, cb: () => void): void {
    // Cap button width so two buttons fit side-by-side on narrow screens
    const BW = Math.min(164, Math.floor((this.screenW - 48) / 2)), BH = 40;

    const g = new Graphics();
    const draw = (hover: boolean) => {
      g.clear();
      g.beginFill(hover ? BTN_HOVER : BTN_BG, 0.96);
      g.lineStyle(1, hover ? colour : DIM_RED, hover ? 0.85 : 0.4);
      g.drawRoundedRect(0, 0, BW, BH, 3);
      g.endFill();
      g.lineStyle(0);
    };
    draw(false);
    g.position.set(cx - BW / 2, cy - BH / 2);
    g.eventMode = 'static';
    g.cursor    = 'pointer';
    g.on('pointerover',  () => draw(true));
    g.on('pointerout',   () => draw(false));
    g.on('pointerdown',  cb);
    this.container.addChild(g);

    const txt = new Text(label, new TextStyle({
      fontFamily: 'Menlo, Consolas, monospace',
      fontSize: 13, fontWeight: 'bold',
      fill: `#${colour.toString(16).padStart(6, '0')}`,
      letterSpacing: 2,
    }));
    txt.anchor.set(0.5, 0.5);
    txt.position.set(cx, cy);
    txt.eventMode = 'none';
    this.container.addChild(txt);
  }

  private formatTime(seconds: number): string {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}m ${s.toString().padStart(2, '0')}s`;
  }
}
